import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Tag, Search } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { Loader } from '../../icons/Loader';
import AnimatedEmptyState from '../../components/ui/AnimatedEmptyState';
import ScrollReveal from '../../components/ui/ScrollReveal';
import ScrollRevealStack from '../../components/ui/ScrollRevealStack';

const BlogList = () => {
  const { blogs, blogsLoading } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const blogPosts = (blogs || []).filter((blog) => blog.status === 'published');

  const categories = ['all', 'Pet Care Tips', 'Dog Care', 'Cat Care', 'Training', 'Health'];

  const filteredPosts = blogPosts.filter((post) => {
    const matchesSearch =
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPost = blogPosts.find((post) => post.featured);

  return (
    <div className="pf-page-wrap pb-16">
      <div className="pf-section-hero py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="left" className="text-center max-w-3xl mx-auto">
            <span className="pf-eyebrow pf-eyebrow--on-dark mb-3">PetsFostering blog</span>
            <h1 className="pf-display text-4xl md:text-5xl text-white mb-4">Pet care insights</h1>
            <p className="text-xl text-blue-100/80 mb-8">
              Expert tips, guides, and advice for pet parents
            </p>

            <div className="max-w-2xl mx-auto input-icon-wrap">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--pf-ink-muted)] w-5 h-5" />
              <input
                type="search"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pf-input pl-12 bg-white"
                aria-label="Search blog articles"
              />
            </div>
          </ScrollReveal>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {blogsLoading ? (
          <div className="py-20 flex justify-center">
            <Loader variant="dog" label="Loading articles" />
          </div>
        ) : blogPosts.length === 0 ? (
          <AnimatedEmptyState
            icon="loader-running-cat"
            title="No articles yet"
            description="We're preparing pet care guides and tips. Check back soon."
          />
        ) : (
          <>
            <ScrollReveal direction="left" className="mb-8">
              <div className="flex flex-wrap gap-2 justify-center">
                {categories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border-2 ${
                      selectedCategory === category
                        ? 'bg-[var(--pf-accent)] text-white border-[var(--pf-accent)]'
                        : 'bg-[var(--pf-surface)] text-[var(--pf-ink-muted)] border-[var(--pf-border)] hover:border-[var(--pf-accent)] hover:text-[var(--pf-accent)]'
                    }`}
                  >
                    {category === 'all' ? 'All topics' : category}
                  </button>
                ))}
              </div>
            </ScrollReveal>

            {featuredPost && selectedCategory === 'all' && !searchTerm && (
              <ScrollReveal direction="right" className="mb-12">
                <article className="pf-organic-card overflow-hidden">
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="h-64 md:h-auto min-h-[16rem]">
                      <img
                        src={featuredPost.image}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-6 md:p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] text-xs font-semibold px-3 py-1 rounded-full">
                          Featured
                        </span>
                        <span className="text-[var(--pf-ink-muted)] text-sm">{featuredPost.category}</span>
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-[var(--pf-ink)] mb-3">
                        {featuredPost.title}
                      </h2>
                      <p className="text-[var(--pf-ink-muted)] mb-4 leading-relaxed">{featuredPost.excerpt}</p>
                      <div className="flex items-center gap-4 text-sm text-[var(--pf-ink-muted)] mb-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(featuredPost.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {featuredPost.readTime}
                        </span>
                      </div>
                      <Link
                        to={`/blog/${featuredPost.slug}`}
                        className="inline-flex items-center gap-2 text-[var(--pf-accent)] font-semibold hover:opacity-80 group w-fit"
                      >
                        Read article
                        <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                </article>
              </ScrollReveal>
            )}

            {filteredPosts.length > 0 ? (
              <ScrollRevealStack className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" staggerMs={90}>
                {filteredPosts.map((post) => (
                  <article key={post.id} className="pf-organic-card overflow-hidden group">
                    <div className="h-48 overflow-hidden">
                      <img
                        src={post.image}
                        alt=""
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Tag className="w-4 h-4 text-[var(--pf-accent)]" />
                        <span className="text-[var(--pf-accent)] text-sm font-medium">{post.category}</span>
                      </div>
                      <h3 className="text-xl font-bold text-[var(--pf-ink)] mb-2 line-clamp-2">{post.title}</h3>
                      <p className="text-[var(--pf-ink-muted)] text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                      <div className="flex items-center gap-3 text-xs text-[var(--pf-ink-muted)] mb-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.readTime}
                        </span>
                      </div>
                      <Link
                        to={`/blog/${post.slug}`}
                        className="inline-flex items-center gap-2 text-[var(--pf-accent)] font-semibold text-sm group/link hover:opacity-80"
                      >
                        Read more
                        <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                      </Link>
                    </div>
                  </article>
                ))}
              </ScrollRevealStack>
            ) : (
              <AnimatedEmptyState
                icon="loader-running-dog"
                title="No articles found"
                description="Try a different search term or category."
                action={
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('all');
                    }}
                    className="pf-btn-primary"
                  >
                    Clear filters
                  </button>
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogList;
